import { useState, useRef, useEffect, useCallback } from 'react';
import { submitBugReport } from '../api/bugReports';
import { BugIcon } from './icons';
import './BugReportButton.css';

const MAX_SCREENSHOTS = 10;
const MAX_BYTES_PER = 5 * 1024 * 1024;

// Read a File/Blob as { contentType, dataBase64, filename }.
function readAsBase64(file, fallbackName) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      // result is "data:<ctype>;base64,<data>"
      const [, b64] = String(reader.result).split(',');
      resolve({
        filename: file.name || fallbackName,
        contentType: file.type || 'image/png',
        dataBase64: b64,
        bytes: file.size,
      });
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/**
 * variant: "full" renders a labeled button. "icon" renders an icon-only button (for tight toolbars).
 * projectId: optional, included in the report context.
 */
export default function BugReportButton({ variant = 'full', projectId = null, className = '' }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [shots, setShots] = useState([]); // { id, filename, contentType, dataBase64, previewUrl, bytes }
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [submittedOk, setSubmittedOk] = useState(false);
  const fileInputRef = useRef(null);
  const idCounter = useRef(0);

  const reset = useCallback(() => {
    shots.forEach((s) => URL.revokeObjectURL(s.previewUrl));
    setTitle('');
    setDescription('');
    setShots([]);
    setError(null);
    setSubmittedOk(false);
  }, [shots]);

  function closeModal() {
    reset();
    setOpen(false);
  }

  async function addFiles(fileList) {
    setError(null);
    const list = Array.from(fileList);
    if (shots.length + list.length > MAX_SCREENSHOTS) {
      setError(`Maximum ${MAX_SCREENSHOTS} screenshots.`);
      return;
    }
    const next = [];
    for (const f of list) {
      if (!f.type.startsWith('image/')) {
        setError(`Skipping non-image: ${f.name || 'pasted item'}`);
        continue;
      }
      if (f.size > MAX_BYTES_PER) {
        setError(`${f.name || 'image'} exceeds 5 MB.`);
        continue;
      }
      const fallback = `screenshot-${Date.now()}-${idCounter.current++}.png`;
      const data = await readAsBase64(f, fallback);
      next.push({
        id: idCounter.current++,
        previewUrl: URL.createObjectURL(f),
        ...data,
      });
    }
    if (next.length > 0) setShots((prev) => [...prev, ...next]);
  }

  // Accept clipboard paste while the modal is open.
  useEffect(() => {
    if (!open) return;
    function onPaste(e) {
      const items = e.clipboardData?.items || [];
      const files = [];
      for (const item of items) {
        if (item.kind === 'file' && item.type.startsWith('image/')) {
          const f = item.getAsFile();
          if (f) files.push(f);
        }
      }
      if (files.length > 0) {
        e.preventDefault();
        addFiles(files);
      }
    }
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, shots]);

  function removeShot(id) {
    setShots((prev) => {
      const target = prev.find((s) => s.id === id);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((s) => s.id !== id);
    });
  }

  function onDrop(e) {
    e.preventDefault();
    if (e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files);
  }

  async function handleSubmit() {
    if (!title.trim() || !description.trim()) {
      setError('Title and description are required.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await submitBugReport({
        title: title.trim(),
        description: description.trim(),
        screenshots: shots.map(({ filename, contentType, dataBase64 }) => ({
          filename,
          contentType,
          dataBase64,
        })),
        context: {
          url: window.location.href,
          projectId: projectId,
          userAgent: navigator.userAgent,
        },
      });
      setSubmittedOk(true);
    } catch (err) {
      setError(err.message || 'Submission failed.');
    } finally {
      setSubmitting(false);
    }
  }

  const triggerTitle = 'Report a bug or suggest a feature';

  return (
    <>
      <button
        type="button"
        className={`bug-report-trigger bug-report-trigger-${variant} ${className}`}
        onClick={() => setOpen(true)}
        title={triggerTitle}
        aria-label={triggerTitle}
      >
        {variant === 'icon' ? (
          <BugIcon />
        ) : (
          <>
            <span className="dash-nav-icon">
              <BugIcon />
            </span>
            <span className="dash-nav-label">Report bug</span>
          </>
        )}
      </button>

      {open && (
        <div className="bug-report-overlay" onClick={closeModal}>
          <div className="bug-report-modal" onClick={(e) => e.stopPropagation()}>
            <div className="bug-report-header">
              <h3>Report a bug or suggest a feature</h3>
              <button className="bug-report-close" onClick={closeModal} aria-label="Close">
                ✕
              </button>
            </div>

            {submittedOk ? (
              <div className="bug-report-success">
                ✓ Thanks — your report has been sent to support.
              </div>
            ) : (
              <>
                <label className="bug-report-label">
                  Title
                  <input
                    type="text"
                    maxLength={200}
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Short summary"
                    disabled={submitting}
                  />
                </label>

                <label className="bug-report-label">
                  Description
                  <textarea
                    rows={6}
                    maxLength={10000}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="What happened? What did you expect to happen? Steps to reproduce, if any."
                    disabled={submitting}
                  />
                </label>

                <div
                  className="bug-report-dropzone"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={onDrop}
                >
                  <p>Paste a screenshot (Ctrl/Cmd+V), drop image files here, or</p>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={submitting}
                  >
                    Browse images
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      addFiles(e.target.files);
                      e.target.value = '';
                    }}
                  />
                  <small>Up to {MAX_SCREENSHOTS} images, 5 MB each.</small>
                </div>

                {shots.length > 0 && (
                  <div className="bug-report-thumbs">
                    {shots.map((s) => (
                      <div key={s.id} className="bug-report-thumb">
                        <img src={s.previewUrl} alt={s.filename} />
                        <button
                          type="button"
                          className="bug-report-thumb-remove"
                          onClick={() => removeShot(s.id)}
                          disabled={submitting}
                          aria-label={`Remove ${s.filename}`}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="bug-report-context">
                  <strong>Auto-included:</strong> current URL, your account, browser info.
                </div>

                {error && <div className="bug-report-error">{error}</div>}

                <div className="bug-report-actions">
                  <button type="button" onClick={closeModal} disabled={submitting}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="bug-report-submit"
                    onClick={handleSubmit}
                    disabled={submitting || !title.trim() || !description.trim()}
                  >
                    {submitting ? 'Sending...' : 'Send report'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
